// Structured, graphical explanation format.
//
// Generated + verified offline (Phase 2/3), stored in Question.richExplanation
// (jsonb), and rendered by <ExplanationView>. The plain Question.explanation
// string is preserved as the fallback for questions without a rich one.
//
// The `diagram` field, when present, is the SAME spec string parseTutorDiagramSpec
// accepts (one of the supported diagram types) — so graphical explanations reuse
// the existing Diagram render pipeline with no new engine.
//
// GENERATION CONTRACT (Phase 2/3) — what a generator MUST satisfy so produced
// explanations preserve the legacy habit of naming the tempting wrong choice and
// ruling it out (user-requested), and so they match the renderer's assumptions:
//   • For every MCQ, `distractors` has exactly ONE entry per WRONG choice —
//     every wrong letter present, no duplicates.
//   • `choice` is a single uppercase letter (A–D) that matches the question's
//     displayed choice order (A = first choice). It MUST NOT be the answer.
//   • Each `why` is choice-specific evidence that rules that option out.
//   • `commonMistake` is the ONE conceptual trap NOT tied to a single choice;
//     omit it when a distractor note already fully captures the gotcha (don't
//     say the same thing twice — the hoisted pick is shown prominently).
//   • SPR questions have no lettered choices, so they omit `distractors` and
//     rely on `commonMistake` alone.
// parseRichExplanation enforces the structural half of this (single A–Z letter,
// never the answer, de-duplicated); the rest is the generator's responsibility.

/** Per-wrong-choice rationale for an MCQ. */
export type DistractorNote = {
  /** The wrong choice this note is about — a single letter, uppercased (e.g. "B"). */
  choice: string
  /** Why that choice is wrong / why it's tempting. Renders through RichText. */
  why: string
}

export type RichExplanation = {
  /** 1–2 sentence approach / key insight. */
  idea: string
  /** Ordered worked steps. Each renders through RichText (math + line breaks). */
  steps: string[]
  /** Optional diagram spec string (parsed via parseTutorDiagramSpec). */
  diagram?: string | null
  /** Correct choice letter or SPR value, e.g. "D" or "144". */
  answer: string
  /** Short note shown next to the answer, e.g. "squared distance = 144". */
  answerNote?: string
  /**
   * Per-wrong-choice rationale (MCQ only). Each entry names a distractor and
   * rules it out. Absent on SPR and on legacy rows. The renderer hoists the
   * note matching the student's own wrong pick.
   */
  distractors?: DistractorNote[]
  /** Optional common-mistake / gotcha line (a conceptual trap, not choice-specific). */
  commonMistake?: string
}

/**
 * Validate an untrusted value (the jsonb column) into a RichExplanation, or
 * null if it doesn't match the shape. Callers fall back to the plain
 * explanation when this returns null, so a malformed/empty rich explanation
 * never breaks the surface.
 */
export function parseRichExplanation(value: unknown): RichExplanation | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const o = value as Record<string, unknown>

  if (typeof o.idea !== 'string' || o.idea.trim() === '') return null
  if (!Array.isArray(o.steps) || o.steps.length === 0) return null
  const steps = o.steps.filter((s): s is string => typeof s === 'string' && s.trim() !== '')
  if (steps.length === 0) return null
  if (typeof o.answer !== 'string' || o.answer.trim() === '') return null

  const diagram = typeof o.diagram === 'string' && o.diagram.trim() !== '' ? o.diagram : null

  // Distractors: keep only entries with a single A–Z choice letter + a non-blank
  // why; never name the correct answer; one note per choice. This makes the
  // parser the trust boundary for the untrusted jsonb column — SPR values (e.g.
  // "7"), multi-char junk, answer-letter contradictions, and duplicate letters
  // are all dropped. Tolerant: never throws, never nulls the whole explanation.
  const correctLetter = o.answer.trim().toUpperCase()
  const distractors: DistractorNote[] = []
  const seenChoices = new Set<string>()
  if (Array.isArray(o.distractors)) {
    for (const d of o.distractors) {
      if (!d || typeof d !== 'object') continue
      const entry = d as Record<string, unknown>
      if (typeof entry.choice !== 'string' || typeof entry.why !== 'string') continue
      const choice = entry.choice.trim().toUpperCase()
      const why = entry.why.trim()
      if (!/^[A-Z]$/.test(choice)) continue
      if (why === '') continue
      if (choice === correctLetter) continue
      if (seenChoices.has(choice)) continue
      seenChoices.add(choice)
      distractors.push({ choice, why })
    }
  }

  return {
    idea: o.idea,
    steps,
    diagram,
    answer: o.answer,
    answerNote:
      typeof o.answerNote === 'string' && o.answerNote.trim() !== '' ? o.answerNote : undefined,
    distractors: distractors.length > 0 ? distractors : undefined,
    commonMistake:
      typeof o.commonMistake === 'string' && o.commonMistake.trim() !== ''
        ? o.commonMistake
        : undefined,
  }
}
